<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
  >
    <ReportTable minWidth="1024px">
      <thead>
        <tr>
          <th><span>Gross production</span></th>
          <th><span>Net production</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Patient collections"
              :tooltip="tooltips['Patient collections']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Insurance collections"
              :tooltip="tooltips['Insurance collections']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Collections ratio"
              :tooltip="tooltips['Collections ratio']"
            />
          </th>
          <th><span>Future treatment scheduled</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <tr>
          <td>{{ formatMoney(clinicMetricsByName.grossProduction) }}</td>
          <td>{{ formatMoney(clinicMetricsByName.netProduction) }}</td>
          <td>{{ formatMoney(clinicMetricsByName.patientPayments) }}</td>
          <td>{{ formatMoney(clinicMetricsByName.insurancePayments) }}</td>
          <td>
            {{ calculateCollectionRatio(clinicMetricsByName.collections, clinicMetricsByName.netProduction) }}
          </td>
          <td>
            {{
              formatScheduledFutureTreatments(
                clinicMetricsByName.scheduledProceduresFee,
                clinicMetricsByName.scheduledProceduresCount,
              )
            }}
          </td>
        </tr>
      </tbody>
    </ReportTable>

    <ReportTable minWidth="760px">
      <thead>
        <tr>
          <th><span>New patients</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Active patients growth"
              :tooltip="tooltips['Active patients growth']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Patients lost"
              :tooltip="tooltips['Patients lost']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="preAppointment %"
              :tooltip="tooltips['preAppointment %']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="reAppointment %"
              :tooltip="tooltips['reAppointment %']"
            />
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <tr>
          <td>{{ clinicMetricsByName.newPatients ?? 0 }}</td>
          <td>{{ clinicMetricsByName.activePatientsGrowth ?? 0 }}</td>
          <td>{{ clinicMetricsByName.patientsLostCount ?? 0 }}</td>
          <td>{{ formatPercentage(clinicMetricsByName.preAppointmentRate ?? 0) }}</td>
          <td>{{ formatPercentage(clinicMetricsByName.reAppointmentRate ?? 0) }}</td>
        </tr>
      </tbody>
    </ReportTable>

    <ReportTable
      v-if="!loading && patientsLost?.length"
      title="Patients Lost"
      className="patients-lost"
      minWidth="760px"
    >
      <thead>
        <tr>
          <th><span>Id</span></th>
          <th><span>Name</span></th>
          <th><span>Email</span></th>
          <th><span>Home phone</span></th>
          <th><span>Work phone</span></th>
          <th><span>Cell phone</span></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="patient in patientsLost"
          :key="patient.ProvNum"
        >
          <td>{{ patient.PatNum }}</td>
          <td>{{ patient.FullName }}</td>
          <td>{{ patient.Email }}</td>
          <td>{{ patient.HmPhone }}</td>
          <td>{{ patient.WkPhone }}</td>
          <td>{{ patient.WirelessPhone }}</td>
        </tr>
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import ReportTable from '../../../components/Reporting/ReportTable.vue';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'ClinicOverview',
  components: {
    MetricDefinitionList,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      clinicMetricsByName: {},
      patientsLost: [],
      loading: false,
      tooltips: {
        'Patient collections': 'Patient payments',
        'Insurance collections': 'Insurance payments',
        'Collections ratio': 'Percentage of dentistry collected divided by the adjusted production',
        'Active patients growth':
          'Increase in number of active patients seen for any reason in the last 18 months since the previous month’s visit',
        'Patients lost': 'Any patient who has not been seen in 18 months + 1 day or more in the last period',
        'preAppointment %':
          'Percentage of total number of patients who are active (seen in the last 18 months) who have an appointment set with this office',
        'reAppointment %':
          'Percentage of patients scheduled at 12:01AM on today’s date, how many of them left with their next visit scheduled at the time the report is run at the end of day',
      },
    };
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getClinicOverviewReport({ start, end });

      if (result.status === 200) {
        this.clinicMetricsByName = result.data.clinicMetricsByName;
        this.patientsLost = result.data.patientsLost ?? [];
      } else {
        this.clinicMetricsByName = {};
        this.patientsLost = [];
      }

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    formatPercentage(number) {
      return Utils.formatPercentage(number);
    },
    formatScheduledFutureTreatments(scheduledProceduresFee, scheduledProceduresCount) {
      return scheduledProceduresFee
        ? `${this.formatMoney(scheduledProceduresFee)} (${scheduledProceduresCount} procedures)`
        : '$0.00';
    },
    calculateCollectionRatio(collections, netProduction) {
      return netProduction ? this.formatPercentage(collections / netProduction) : 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.patients-lost {
  :deep(.table-shadow) {
    max-height: 350px;
    overflow: auto;
  }
}
</style>
